import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { GamificationState, Achievement, AchievementId } from '@/types'
import { getLevelFromXP } from '@/types/gamification.types'

// Initial achievement definitions
const createInitialAchievements = (): Record<AchievementId, Achievement> => ({
  // Goals Category
  first_steps: {
    id: 'first_steps',
    name: 'First Steps',
    description: 'Create your first financial goal',
    category: 'goals',
    icon: 'Flag',
    points: 10,
    unlockedAt: null,
    progress: 0,
    requirement: 1,
  },
  goal_getter: {
    id: 'goal_getter',
    name: 'Goal Getter',
    description: 'Complete your first goal',
    category: 'goals',
    icon: 'CheckCircle',
    points: 25,
    unlockedAt: null,
    progress: 0,
    requirement: 1,
  },
  triple_threat: {
    id: 'triple_threat',
    name: 'Triple Threat',
    description: 'Complete 3 goals',
    category: 'goals',
    icon: 'Award',
    points: 50,
    unlockedAt: null,
    progress: 0,
    requirement: 3,
  },
  goal_master: {
    id: 'goal_master',
    name: 'Goal Master',
    description: 'Complete 10 goals',
    category: 'goals',
    icon: 'Crown',
    points: 100,
    unlockedAt: null,
    progress: 0,
    requirement: 10,
  },
  dream_big: {
    id: 'dream_big',
    name: 'Dream Big',
    description: 'Create a goal over $50,000',
    category: 'goals',
    icon: 'Rocket',
    points: 20,
    unlockedAt: null,
    progress: 0,
    requirement: 1,
  },
  
  // Savings Category
  first_dollar: {
    id: 'first_dollar',
    name: 'First Dollar',
    description: 'Make your first contribution',
    category: 'savings',
    icon: 'DollarSign',
    points: 10,
    unlockedAt: null,
    progress: 0,
    requirement: 1,
  },
  century_club: {
    id: 'century_club',
    name: 'Century Club',
    description: 'Save $100 toward goals',
    category: 'savings',
    icon: 'Coins',
    points: 15,
    unlockedAt: null,
    progress: 0,
    requirement: 100,
  },
  thousand_strong: {
    id: 'thousand_strong',
    name: 'Thousand Strong',
    description: 'Save $1,000 toward goals',
    category: 'savings',
    icon: 'PiggyBank',
    points: 30,
    unlockedAt: null,
    progress: 0,
    requirement: 1000,
  },
  five_figure_saver: {
    id: 'five_figure_saver',
    name: 'Five Figure Saver',
    description: 'Save $10,000 toward goals',
    category: 'savings',
    icon: 'Landmark',
    points: 75,
    unlockedAt: null,
    progress: 0,
    requirement: 10000,
  },
  six_figure_club: {
    id: 'six_figure_club',
    name: 'Six Figure Club',
    description: 'Save $100,000 toward goals',
    category: 'savings',
    icon: 'Trophy',
    points: 200,
    unlockedAt: null,
    progress: 0,
    requirement: 100000,
  },
  
  // Streaks Category
  week_warrior: {
    id: 'week_warrior',
    name: 'Week Warrior',
    description: 'Maintain a 7-day activity streak',
    category: 'streaks',
    icon: 'Flame',
    points: 20,
    unlockedAt: null,
    progress: 0,
    requirement: 7,
  },
  monthly_momentum: {
    id: 'monthly_momentum',
    name: 'Monthly Momentum',
    description: 'Maintain a 30-day activity streak',
    category: 'streaks',
    icon: 'Zap',
    points: 50,
    unlockedAt: null,
    progress: 0,
    requirement: 30,
  },
  quarterly_champion: {
    id: 'quarterly_champion',
    name: 'Quarterly Champion',
    description: 'Maintain a 90-day activity streak',
    category: 'streaks',
    icon: 'Medal',
    points: 100,
    unlockedAt: null,
    progress: 0,
    requirement: 90,
  },
  year_of_dedication: {
    id: 'year_of_dedication',
    name: 'Year of Dedication',
    description: 'Maintain a 365-day activity streak',
    category: 'streaks',
    icon: 'Star',
    points: 500,
    unlockedAt: null,
    progress: 0,
    requirement: 365,
  },
  
  // Milestones Category
  emergency_ready: {
    id: 'emergency_ready',
    name: 'Emergency Ready',
    description: 'Fully fund an emergency fund goal',
    category: 'milestones',
    icon: 'ShieldCheck',
    points: 50,
    unlockedAt: null,
    progress: 0,
    requirement: 1,
  },
  debt_destroyer: {
    id: 'debt_destroyer',
    name: 'Debt Destroyer',
    description: 'Complete a debt payoff goal',
    category: 'milestones',
    icon: 'Swords',
    points: 50,
    unlockedAt: null,
    progress: 0,
    requirement: 1,
  },
  homeward_bound: {
    id: 'homeward_bound',
    name: 'Homeward Bound',
    description: 'Complete a down payment goal',
    category: 'milestones',
    icon: 'Home',
    points: 75,
    unlockedAt: null,
    progress: 0,
    requirement: 1,
  },
  early_bird: {
    id: 'early_bird',
    name: 'Early Bird',
    description: 'Complete a goal before its deadline',
    category: 'milestones',
    icon: 'Clock',
    points: 30,
    unlockedAt: null,
    progress: 0,
    requirement: 1,
  },
  
  // Engagement Category
  explorer: {
    id: 'explorer',
    name: 'Explorer',
    description: 'Visit all pages in the app',
    category: 'engagement',
    icon: 'Compass',
    points: 15,
    unlockedAt: null,
    progress: 0,
    requirement: 10, // Number of pages
  },
  data_driven: {
    id: 'data_driven',
    name: 'Data Driven',
    description: 'Link a goal to an account',
    category: 'engagement',
    icon: 'Link',
    points: 15,
    unlockedAt: null,
    progress: 0,
    requirement: 1,
  },
  planner_pro: {
    id: 'planner_pro',
    name: 'Planner Pro',
    description: 'Have 5 active goals simultaneously',
    category: 'engagement',
    icon: 'ListChecks',
    points: 25,
    unlockedAt: null,
    progress: 0,
    requirement: 5,
  },
})

const initialState: GamificationState = {
  level: 1,
  currentXP: 0,
  totalXP: 0,
  achievements: createInitialAchievements(),
  currentStreak: 0,
  longestStreak: 0,
  lastActivityDate: null,
  weeklyGoalContributions: 0,
  visitedPages: [],
}

const gamificationSlice = createSlice({
  name: 'gamification',
  initialState,
  reducers: {
    unlockAchievement: (state, action: PayloadAction<AchievementId>) => {
      const achievement = state.achievements[action.payload]
      if (achievement && !achievement.unlockedAt) {
        achievement.unlockedAt = new Date().toISOString()
        achievement.progress = achievement.requirement
        state.totalXP += achievement.points
        state.currentXP = state.totalXP
        state.level = getLevelFromXP(state.totalXP).level
      }
    },
    
    updateAchievementProgress: (state, action: PayloadAction<{
      id: AchievementId
      progress: number
    }>) => {
      const { id, progress } = action.payload
      const achievement = state.achievements[id]
      
      if (achievement && !achievement.unlockedAt) {
        achievement.progress = Math.min(progress, achievement.requirement)
        
        // Auto-unlock if requirement met
        if (achievement.progress >= achievement.requirement) {
          achievement.unlockedAt = new Date().toISOString()
          state.totalXP += achievement.points
          state.currentXP = state.totalXP
          state.level = getLevelFromXP(state.totalXP).level
        }
      }
    },
    
    incrementAchievementProgress: (state, action: PayloadAction<{
      id: AchievementId
      amount?: number
    }>) => {
      const { id, amount = 1 } = action.payload
      const achievement = state.achievements[id]
      
      if (achievement && !achievement.unlockedAt) {
        achievement.progress = Math.min(achievement.progress + amount, achievement.requirement)
        
        // Auto-unlock if requirement met
        if (achievement.progress >= achievement.requirement) {
          achievement.unlockedAt = new Date().toISOString()
          state.totalXP += achievement.points
          state.currentXP = state.totalXP
          state.level = getLevelFromXP(state.totalXP).level
        }
      }
    },
    
    addXP: (state, action: PayloadAction<number>) => {
      state.totalXP += action.payload
      state.currentXP = state.totalXP
      state.level = getLevelFromXP(state.totalXP).level
    },
    
    recordActivity: (state) => {
      const today = new Date().toISOString().split('T')[0]
      const lastDate = state.lastActivityDate?.split('T')[0]
      
      if (lastDate === today) {
        // Already recorded activity today
        return
      }
      
      if (lastDate) {
        const lastDateObj = new Date(lastDate)
        const todayObj = new Date(today)
        const diffDays = Math.floor((todayObj.getTime() - lastDateObj.getTime()) / (1000 * 60 * 60 * 24))
        
        if (diffDays === 1) {
          // Consecutive day
          state.currentStreak += 1
        } else if (diffDays > 1) {
          // Streak broken
          state.currentStreak = 1
        }
      } else {
        // First activity
        state.currentStreak = 1
      }
      
      state.lastActivityDate = new Date().toISOString()
      state.longestStreak = Math.max(state.longestStreak, state.currentStreak)
      state.weeklyGoalContributions += 1
      
      // Update streak achievements
      const streakAchievements: { id: AchievementId; requirement: number }[] = [
        { id: 'week_warrior', requirement: 7 },
        { id: 'monthly_momentum', requirement: 30 },
        { id: 'quarterly_champion', requirement: 90 },
        { id: 'year_of_dedication', requirement: 365 },
      ]
      
      streakAchievements.forEach(({ id, requirement }) => {
        const achievement = state.achievements[id]
        if (achievement && !achievement.unlockedAt) {
          achievement.progress = state.currentStreak
          if (state.currentStreak >= requirement) {
            achievement.unlockedAt = new Date().toISOString()
            state.totalXP += achievement.points
            state.currentXP = state.totalXP
            state.level = getLevelFromXP(state.totalXP).level
          }
        }
      })
    },
    
    visitPage: (state, action: PayloadAction<string>) => {
      if (!state.visitedPages.includes(action.payload)) {
        state.visitedPages.push(action.payload)
        
        // Update explorer achievement
        const explorerAchievement = state.achievements.explorer
        if (explorerAchievement && !explorerAchievement.unlockedAt) {
          explorerAchievement.progress = state.visitedPages.length
          if (state.visitedPages.length >= explorerAchievement.requirement) {
            explorerAchievement.unlockedAt = new Date().toISOString()
            state.totalXP += explorerAchievement.points
            state.currentXP = state.totalXP
            state.level = getLevelFromXP(state.totalXP).level
          }
        }
      }
    },
    
    resetWeeklyContributions: (state) => {
      state.weeklyGoalContributions = 0
    },
    
    setGamificationData: (state, action: PayloadAction<Partial<GamificationState>>) => {
      return { ...state, ...action.payload }
    },
    
    resetGamification: () => initialState,
  },
})

export const {
  unlockAchievement,
  updateAchievementProgress,
  incrementAchievementProgress,
  addXP,
  recordActivity,
  visitPage,
  resetWeeklyContributions,
  setGamificationData,
  resetGamification,
} = gamificationSlice.actions

export default gamificationSlice.reducer
